import type { ArenaState } from "@townsy/contracts";
import { basesText, clockText, downText, formatClock, inkOn, momentLabel, periodText } from "../model";
import type { Ctx } from "./paint";

/**
 * The stadium's big screen (ADR-0044): both teams with their colours and scores, the period and the clock (or the
 * inning, outs, count and bases), the next game's countdown before kickoff, FINAL after the whistle, and a full-screen
 * flash for a big moment. Painted on a canvas (DynamicTexture) only when what it shows changes.
 */
export function scoreboardKey(state: ArenaState | null, kickoffSeconds: number, momentOn: boolean): string {
  if (!state) return "none";
  const b = state.board;
  return [
    state.gameNo,
    state.phase,
    state.home.name,
    state.away.name,
    b.score.join("-"),
    b.periodLabel,
    b.clock === null ? "" : Math.floor(b.clock),
    b.status,
    b.baseball ? `${b.baseball.outs}${b.baseball.balls}${b.baseball.strikes}${b.baseball.bases.join("")}` : "",
    b.football ? `${b.football.down}${b.football.toGo}${b.football.ballOn}` : "",
    b.shootout ? `${b.shootout.home}${b.shootout.away}` : "",
    state.phase === "live" ? "" : kickoffSeconds,
    momentOn && state.moment ? state.moment.id : "",
  ].join("|");
}

function fitText(c: Ctx, s: string, maxWidth: number, size: number, weight = 800): void {
  let px = size;
  c.font = `${weight} ${px}px "Arial Black", Arial, sans-serif`;
  while (px > 10 && c.measureText(s).width > maxWidth) {
    px -= 2;
    c.font = `${weight} ${px}px "Arial Black", Arial, sans-serif`;
  }
}

export function paintScoreboard(c: Ctx, w: number, h: number, state: ArenaState | null, kickoffSeconds: number, momentOn: boolean): void {
  c.fillStyle = "#07090F";
  c.fillRect(0, 0, w, h);
  // LED dot grain.
  c.fillStyle = "rgba(255,255,255,0.025)";
  for (let y = 0; y < h; y += 6) for (let x = (y / 6) % 2 ? 3 : 0; x < w; x += 6) c.fillRect(x, y, 2, 2);
  if (!state) {
    c.fillStyle = "#F2B92C";
    c.textAlign = "center";
    c.textBaseline = "middle";
    fitText(c, "TOWNSY SPORTS PARK", w * 0.9, h * 0.16);
    c.fillText("TOWNSY SPORTS PARK", w / 2, h / 2);
    return;
  }
  const m = state.moment;
  if (momentOn && m) {
    const team = m.team === 0 ? state.home : m.team === 1 ? state.away : null;
    c.fillStyle = team?.primary ?? "#14171F";
    c.fillRect(0, 0, w, h);
    c.strokeStyle = team?.secondary ?? "#F2B92C";
    c.lineWidth = h * 0.05;
    c.strokeRect(h * 0.04, h * 0.04, w - h * 0.08, h - h * 0.08);
    c.fillStyle = inkOn(team?.primary ?? "#14171F");
    c.textAlign = "center";
    c.textBaseline = "middle";
    const label = momentLabel(m.kind);
    fitText(c, label, w * 0.86, h * 0.36, 900);
    c.fillText(label, w / 2, h * 0.42);
    fitText(c, m.text, w * 0.86, h * 0.1, 700);
    c.fillText(m.text, w / 2, h * 0.76);
    return;
  }
  const b = state.board;
  const score = state.phase === "final" && state.last ? state.last.score : b.score;
  const col = (i: 0 | 1) => (i === 0 ? state.home : state.away);
  c.textBaseline = "middle";
  // Team rows: a colour bar, the name, the score.
  for (const i of [0, 1] as const) {
    const t = col(i);
    const y0 = h * (0.06 + i * 0.3);
    const rowH = h * 0.26;
    c.fillStyle = t.primary;
    c.fillRect(w * 0.03, y0, w * 0.025, rowH);
    c.fillStyle = t.secondary;
    c.fillRect(w * 0.055, y0, w * 0.008, rowH);
    c.fillStyle = "#F4F6FB";
    c.textAlign = "left";
    fitText(c, t.name.toUpperCase(), w * 0.62, rowH * 0.62);
    c.fillText(t.name.toUpperCase(), w * 0.08, y0 + rowH / 2);
    c.fillStyle = "#F2B92C";
    c.textAlign = "right";
    c.font = `900 ${Math.round(rowH * 0.95)}px "Arial Black", Arial, sans-serif`;
    c.fillText(String(score[i]), w * 0.96, y0 + rowH / 2 + rowH * 0.04);
    if (state.phase === "live" && b.possession === i && state.sport !== "baseball") {
      c.fillStyle = "#F4F6FB";
      c.beginPath();
      const px = w * 0.73;
      const py = y0 + rowH / 2;
      c.moveTo(px, py - rowH * 0.16);
      c.lineTo(px + rowH * 0.22, py);
      c.lineTo(px, py + rowH * 0.16);
      c.fill();
    }
  }
  // The bottom band: period and clock, or the countdown / FINAL.
  const y = h * 0.82;
  c.fillStyle = "#121726";
  c.fillRect(0, h * 0.67, w, h * 0.33);
  c.textAlign = "center";
  if (state.phase === "pregame") {
    c.fillStyle = "#F2B92C";
    const s = `NEXT GAME ${formatClock(kickoffSeconds)}`;
    fitText(c, s, w * 0.9, h * 0.2);
    c.fillText(s, w / 2, y);
    return;
  }
  if (state.phase === "final") {
    c.fillStyle = "#F2B92C";
    const s = `FINAL${state.last?.suffix ? ` / ${state.last.suffix}` : ""}  ·  NEXT ${formatClock(kickoffSeconds)}`;
    fitText(c, s, w * 0.92, h * 0.2);
    c.fillText(s, w / 2, y);
    return;
  }
  const period = periodText(state.sport, b, state.phase).toUpperCase();
  const clock = clockText(state.sport, b, state.phase);
  const extra = state.sport === "football" ? downText(b).toUpperCase() : state.sport === "baseball" ? basesText(b).toUpperCase() : (b.status || "").toUpperCase();
  c.fillStyle = "#F2B92C";
  c.textAlign = "left";
  fitText(c, period, w * 0.3, h * 0.17);
  c.fillText(period, w * 0.04, y);
  c.fillStyle = "#FFFFFF";
  c.textAlign = "center";
  fitText(c, clock, w * 0.3, h * 0.22, 900);
  c.fillText(clock, w * 0.5, y);
  c.fillStyle = "#C9D1E3";
  c.textAlign = "right";
  fitText(c, extra, w * 0.3, h * 0.12, 700);
  c.fillText(extra, w * 0.96, y);
}
